import React, { useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { ViewPlugin, Decoration, MatchDecorator, EditorView } from "@codemirror/view";
import { startCompletion } from "@codemirror/autocomplete";
import { javascriptLanguage } from "@codemirror/lang-javascript";

const variableDeco = Decoration.mark({ class: "fm-variable" });

function createHighlightPlugin() {
    const variableMatcher = new MatchDecorator({
        regexp: /\[[^\]]+\]/g,
        decoration: variableDeco,
    });
    return [
        ViewPlugin.fromClass(
            class {
                constructor(view) { this.decorations = variableMatcher.createDeco(view); }
                update(update) { this.decorations = variableMatcher.updateDeco(update, this.decorations); }
            },
            { decorations: (v) => v.decorations }
        )
    ];
}

function createVariableCompletion(variables) {
    return javascriptLanguage.data.of({
        autocomplete: (context) => {
            const before = context.matchBefore(/\[[^\]]*$/);
            if (!before) return null;

            const typed = before.text.slice(1).toLowerCase();
            const options = variables
                .filter((v) => v.toLowerCase().includes(typed))
                .map((v) => ({
                    label: v,
                    displayLabel: `[${v}]`,
                    apply: `[${v}]`,
                    type: "variable",
                    boost: 99,
                }));

            if (options.length === 0) return null;

            return {
                from: before.from,
                options,
                filter: false,
                validFor: /^[^\]]*$/,
            };
        },
    });
}

const baseTheme = EditorView.theme({
    "&": {
        fontSize: "14px",
        border: "1px solid #696969",
        borderRadius: "3px",
        backgroundColor: "#fafafa",
    },
    "&.cm-focused": {
        outline: "2px solid #362B7A",
        outlineOffset: "-1px",
    },
    ".cm-content": {
        fontFamily: "'Fira Code', 'Fira Mono', 'Consolas', 'Monaco', monospace",
        padding: "10px 14px",
        minHeight: "120px",
    },
    ".cm-line": {
        lineHeight: "1.6",
    },
    ".cm-gutters": {
        display: "none",
    },
    ".fm-variable": {
        color: "#362B7A",
        fontWeight: "600",
        backgroundColor: "rgba(54, 43, 122, 0.08)",
        borderRadius: "3px",
        padding: "1px 0",
    },
    ".cm-tooltip-autocomplete": {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "13px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    },
    ".cm-tooltip-autocomplete ul li": {
        padding: "6px 12px",
    },
    ".cm-tooltip-autocomplete ul li[aria-selected]": {
        backgroundColor: "#362B7A",
        color: "#fff",
    },
});

const FormulaEditor = ({ label, value, change, variables = [] }) => {
    const extensions = useMemo(() => [
        baseTheme,
        javascriptLanguage,
        ...createHighlightPlugin(),
        createVariableCompletion(variables),
        EditorView.lineWrapping,
        EditorView.updateListener.of((update) => {
            if (!update.docChanged) return;
            if (!update.transactions.some(tr => tr.isUserEvent("input"))) return;
            update.changes.iterChanges((_fA, _tA, _fB, _tB, inserted) => {
                if (inserted.sliceString(0).includes("[")) {
                    setTimeout(() => startCompletion(update.view), 0);
                }
            });
        }),
    ], [variables]);

    const safeValue = typeof value === "string" ? value : (value == null ? "" : String(value));

    return (
        <div className={`formula-editor-group${label ? ' has-label' : ''}`}>
            {label && <label>{label}</label>}
            <CodeMirror
                value={safeValue}
                onChange={change}
                extensions={extensions}
                basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: true,
                    highlightSelectionMatches: true,
                    bracketMatching: true,
                    closeBrackets: false,
                    indentOnInput: true,
                    autocompletion: true,
                    highlightActiveLineGutter: true,
                    highlightSpecialChars: true,
                    history: true,
                    drawSelection: true,
                    dropCursor: true,
                    allowMultipleSelections: true,
                    syntaxHighlighting: true,
                    defaultKeymap: true,
                    historyKeymap: true,
                    foldKeymap: true,
                    completionKeymap: true,
                    searchKeymap: true,
                    lintKeymap: true,
                    closeBracketsKeymap: true,
                }}
            />
        </div>
    );
};

export default FormulaEditor;
